import parentApi from "store/parentApi"
import { capitalizeFirstLetter, convertQueryObjToString, sanitizeHTML } from "utils"
import { dateTimeFunction } from "utils/date"
import bookingPlatformTransformer from "./transformResponse/bookingPlatformTransformer"
import paymentStatusTransformer from "./transformResponse/paymentStatusTransformer"
import policiesTransformer from "./transformResponse/policiesTransformer"
import { projectCategoriesTransformer } from "./transformResponse/projectCategories"
import { projectPromoTagsTransformer } from "./transformResponse/projectPromoTagsTransformer"
import {
  projectDetailTransformer,
  unitSubTypesBasedTranformer,
  unitTypesWithSubTypesTransformer,
} from "./transformResponse/projectsTransformer"
import projectTypeLocationTransformer from "./transformResponse/projectTypeLocation"
import projectUnitTypesTransformer from "./transformResponse/projectUnitTypesTransformer"
import { billingTypes } from "features/shared/data"
import { localisedValue } from "utils"
import projectDevelopersTransformer from "./transformResponse/projectDevelopersTransformer"

const coverPageMapper = (keys, covers) => {
  return covers.reduce((result, cover, index) => {
    if (cover?.fileList?.length > 0) {
      result.push({
        key: keys[index],
        fileName: cover?.fileList?.[0]?.name,
        fileExtension: "." + cover?.fileList?.[0]?.type.split("/").pop(),
        s3FileKey: cover?.fileList?.[0]?.response?.key,
      })
    }
    return result
  }, [])
}

const projectApis = parentApi.injectEndpoints({
  endpoints: build => ({
    getListingProjects: build.query({
      query: ({ activeTab, ...params }) => {
        let obj = { ...params }

        if (obj.billingType) {
          obj["billingTypeKey"] = billingTypes()[obj.billingType]?.key
          delete obj["billingType"]
        }
        if (obj.areaRange) {
          let area = obj.areaRange.split(",")
          obj["minArea"] = area[0]
          obj["maxArea"] = area[1]
          delete obj["areaRange"]
        }
        if (obj.instalmentRange) {
          let instalment = obj.instalmentRange.split(",")
          obj["minInstalment"] = instalment[0]
          obj["maxInstalment"] = instalment[1]
          delete obj["instalmentRange"]
        }
        if (obj.priceRange) {
          let price = obj.priceRange.split(",")
          obj["minPrice"] = price[0]
          obj["maxPrice"] = price[1]
          delete obj["priceRange"]
        }
        return `/projects?${convertQueryObjToString({ ...obj, status: !activeTab ? "on" : activeTab })}`
      },
      providesTags: ["projects-listing"],
    }),

    getUnitTypesWithSubTypes: build.query({
      query: () => `/types/typesWithChildren`,
      transformResponse: (res, meta, arg) => {
        return arg ? unitSubTypesBasedTranformer(res) : unitTypesWithSubTypesTransformer(res)
      },
    }),

    getAreaBasedOnLocationAndUnit: build.query({
      query: obj => `/projects/${obj?.projectId}/areas?${convertQueryObjToString(obj?.queryObj)}`,
      transformResponse: data =>
        data?.map((item, index) => ({ ...item, label: Math.round(item), value: `${item}-key` })),
    }),

    getProjectDetailForm: build.query({
      query: projectId => `/projects/detail/${projectId}`,
      transformResponse: res => projectDetailTransformer(res),
      providesTags: ["project-detail"],
    }),

    getDeveloperPortalStepperPrefill: build.query({
      query: developerId => `/developer-portal/stepper-prefill/${developerId}`,
      // API returns { success, data: { developer, projects, registration, projectConfigs } }
      transformResponse: res => res?.data ?? res,
    }),

    submitDeveloperPortalStepper: build.mutation({
      query: body => ({
        url: `/developer-portal/stepper`,
        method: "POST",
        body,
      }),
      transformResponse: res => res?.data ?? res,
      invalidatesTags: (result, error) => (error ? [] : ["project-detail", "projects-listing"]),
    }),

    /** Body: `{ projects: [{ projectId, inventorySync, bookingVisibility, paymentVisibility }] }` */
    updateDeveloperPortalProjects: build.mutation({
      query: ({ developerId, body }) => ({
        url: `/developer-portal/${developerId}/projects`,
        method: "PUT",
        body,
      }),
      transformResponse: res => res?.data ?? res,
      invalidatesTags: (result, error) => (error ? [] : ["project-detail", "projects-listing"]),
    }),

    disconnectDeveloperPortal: build.mutation({
      query: ({ developerId }) => ({
        url: `/developer-portal/${developerId}/disable`,
        method: "PUT",
      }),
      transformResponse: res => res?.data ?? res,
      invalidatesTags: (result, error) => (error ? [] : ["project-detail", "projects-listing"]),
    }),

    getProjectBasicInfo: build.query({
      query: projectId => `/projects/${projectId}/basic`,
      // transformResponse: res => projectDetailTransformer(res),
    }),

    getProjectContractExpiryRules: build.query({
      query: params => `/projects/customExpiryRules?${convertQueryObjToString(params)}`,
      providesTags: ["project-expiry-rules"],
    }),

    getPaymentModesInvoiceTax: build.query({
      query: params => `/dispositions?${convertQueryObjToString(params)}`,
      transformResponse: data => {
        const transformedData = data?.map(item => {
          return {
            ...item,
            label: item?.name,
            value: item?.id?.toString(),
          }
        })
        return transformedData
      },
    }),

    getProjectPaymentMode: build.query({
      query: () => `/paymentModes`,
      transformResponse: data => {
        const transformedData = data?.map(item => {
          return {
            ...item,
            label: item?.name,
            value: item?.id?.toString(),
          }
        })
        return transformedData
      },
    }),

    getProjectDesignations: build.query({
      query: () => "/userDesignations/teamTypeWiseDesignations?teamTypeKey=sales&designationKeys=CEO",
      transformResponse: res => res.map(e => ({ ...e, id: e.id.toString(), label: e?.name, value: e.id.toString() })),
    }),

    getPolicies: build.query({
      query: () => `/projectBillingPolicies`,
      transformResponse: data => policiesTransformer(data),
    }),

    getPaymentStatuses: build.query({
      query: () => `/paymentStatuses`,
      transformResponse: data => paymentStatusTransformer(data),
    }),

    addProjectBasicInfo: build.mutation({
      query: ({ projectId, ...rest }) => {
        return {
          url: projectId ? `/projects/${projectId}/basic` : `/projects/basic`,
          method: projectId ? "PUT" : "POST",
          body: rest,
        }
      },
      invalidatesTags: (result, error, args) => (result ? ["project-detail"] : []),
    }),
    updateProjectSettings: build.mutation({
      query: ({ projectId, financialId, ...rest }) => {
        let body = {
          ...(projectId && { projectId }),
          projectFinancial: {
            ppaHasRightToSell: JSON.parse(rest?.ppaExpiry),
            ...(JSON.parse(rest?.ppaExpiry) && {
              ppaStartDate: dateTimeFunction({ date: rest?.ppaStartDate, Default: true }).toISOString(),
            }),
            ...(JSON.parse(rest?.ppaExpiry) && {
              ppaEndDate: dateTimeFunction({ date: rest?.ppaEndDate, Default: true }).toISOString(),
            }),
            ...(financialId && { id: financialId }),
          },
          affiliateVisibility: !!rest?.investorHubVisibility?.includes("affiliateVisibility"),
          investorHubVisibility: !!rest?.investorHubVisibility?.includes("investorHubVisibility"),
          status: rest?.status,
          allowProvisionalUnit: rest?.allowProvisionalUnit,
          allowFractionalSale: rest?.allowFractionalSale === "true" ? true : false,
        }
        return {
          url: `/projects/${projectId}/settings`,
          method: "PUT",
          body,
        }
      },
      invalidatesTags: (result, error, args) => (result ? ["project-detail", "projects-listing"] : []),
    }),

    updateProjectMedia: build.mutation({
      query: ({ projectId, ...rest }) => {
        return {
          url: `/projects/${projectId}/media`,
          method: "PUT",
          body: rest,
        }
      },
      invalidatesTags: (result, error, args) => (result ? ["project-detail", "projects-listing"] : []),
    }),

    getProjectTypes: build.query({
      query: params => `/types/projectTypes/${params.id}`,
      transformResponse: data => projectUnitTypesTransformer(data),
    }),

    getProjectClassifications: build.query({
      query: (flag = false) => `/projects/classifications`,
      transformResponse: (data, _, flag) =>
        data?.categories?.map(item => ({
          ...item,
          key: item?.id,
          label: localisedValue({ object: item, key: "name" }),
          value: flag ? item?.id?.toString() : item?.key,
        })),
    }),

    getProjectTypeLocations: build.query({
      query: params => `/projectSections/project/${params.id}`,
      transformResponse: data => projectTypeLocationTransformer(data),
    }),

    getProjectPhaseListing: build.query({
      query: params => `/projects/phases?${convertQueryObjToString(params)}`,
      providesTags: ["project-phase"],
    }),

    getUnitTypesByListing: build.query({
      query: params => `/types/typesByListingStatus/${params?.projectId}?unitStatus=available`,
      transformResponse: data => data?.map(item => ({ ...item, label: item?.name, value: item?.typeId })),
    }),
    getProjectSectionUnitType: build.query({
      query: params =>
        `/projectSections/projectSectionsByUnitType?projectId=${params.projectId}&${params.showAvailable}&${params.unitTypeIds}`,
    }),

    getProjectPhaseFormData: build.query({
      query: params => `/projects/phases/${params.id}`,
    }),

    deleteProjectPhase: build.mutation({
      query: params => {
        return { url: `/projects/phases/${params.id}`, method: "DELETE", body: { projectId: params.projectId } }
      },
      invalidatesTags: ["project-phase"],
    }),

    addProjectPhase: build.mutation({
      query: payload => {
        let transformedTargetbreak = payload?.targetBreakdownList?.map(item => ({
          ...item,
          locations: item?.locations?.map(item => item?.value),
          targetTypes: [item?.targetTypes?.value],
        }))

        let body = {
          action: payload?.actionType,
          endDate: dateTimeFunction({ date: payload?.endDate, timeZone: true, apiRequest: true }),
          name: payload?.phaseName,
          projectId: payload?.projectId,
          sendNotifications: payload?.notification,
          startDate: dateTimeFunction({ date: payload?.startDate, timeZone: true, apiRequest: true }),
          targetBreakdowns: transformedTargetbreak || [],
          targetType: payload?.targetType,
          targetValue: payload?.targetValue,
          ...(payload?.phaseId && { id: payload?.phaseId }),
        }
        return {
          url: payload?.phaseId ? `/projects/phase/${payload?.phaseId}` : `/projects/addPhase`,
          method: payload?.phaseId ? "PUT" : "POST",
          body: body,
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["project-phase"] : []
      },
    }),

    addEditProjectBookingForm: build.mutation({
      query: ({ data, projectId }) => {
        let transformedPossession = data?.possession?.map(item => ({
          unitType: item?.unitType?.value || -1,
          location: item?.location?.value || -1,
          possession: item?.possession,
          possessionUnit: item?.possessionUnit?.value || item?.possessionUnit,
        }))
        let timeBasedInstalments = data?.timeBasedInstallment?.map(item => ({
          unitType: item?.unitType?.value || -1,
          location: item?.location?.value || -1,
          instalment: +item?.installment,
        }))
        let constructionBasedInstalments = data?.constructionBasedInstallment?.map(item => ({
          milestoneName: item?.milestoneName,
          milestonePercentage: +item?.milestonePercentage,
        }))
        let rentalAdjustments = data?.rentalAdjustments?.map(item => ({
          accumulatedDpPercentage: +(+item?.accumulatedDealPrice).toFixed(2),
          unitType: item?.unitType?.value || -1,
          location: item?.location?.value || -1,
          rentalPercentage: +(+item?.rentalAmountPercentage).toFixed(2),
        }))
        const installments = []
        const installmentType = []

        if (data?.isConstructionBasedInstallment) {
          installments.push({
            type: "construction_based",
            instalments: constructionBasedInstalments,
          })
          installmentType.push("construction_based")
        }

        if (data?.isTimeBasedInstallment) {
          installments.push({
            type: "time_based",
            instalments: timeBasedInstalments,
          })
          installmentType.push("time_based")
        }
        let termsAndConditions =
          data?.paymentTermsAndConditions === "<p><br></p>" || data?.paymentTermsAndConditions === null
            ? ""
            : data?.paymentTermsAndConditions

        let transformedTermsAndConditions =
          data?.transformedPaymentTermsAndConditions === "<p><br></p>" ||
          data?.transformedPaymentTermsAndConditions === null
            ? ""
            : data?.transformedPaymentTermsAndConditions

        let termsAndConditionsAR =
          data?.paymentTermsAndConditionsAR === "<p><br></p>" || data?.paymentTermsAndConditionsAR === null
            ? ""
            : data?.paymentTermsAndConditionsAR
        let transformedTermsAndConditionsAR =
          data?.transformedPaymentTermsAndConditionsAR === "<p><br></p>" ||
          data?.transformedPaymentTermsAndConditionsAR === null
            ? ""
            : data?.transformedPaymentTermsAndConditionsAR

        let body = {
          possessions: transformedPossession,
          possessionFrom: data?.possessionFrom,
          isInstalmentsRequired: data?.allowInstallments,
          hasAdjustableRent: data?.isRental ?? false,
          templateType: data?.templateType,
          paymentTermsAndConditions: {
            termsAndConditions: sanitizeHTML(termsAndConditions),
            transformedTermsAndConditions: transformedTermsAndConditions,
          },
          ...(!data?.templateType && {
            paymentTermsAndConditionsAR: {
              termsAndConditions: sanitizeHTML(termsAndConditionsAR),
              transformedTermsAndConditions: transformedTermsAndConditionsAR,
            },
          }),
          finishing: data?.finishing?.[0]?.finishingAmount ?? null,
          finishingUnit: data?.finishing?.[0]?.finishingUnit?.value ?? null,
        }

        if (data?.allowInstallments) {
          body.instalments = installments
          body.instalmentType = installmentType
        }

        if (data?.allowInstallments && data?.isTimeBasedInstallment && data?.isRental) {
          body.rentalAdjustments = rentalAdjustments
          body.rentalAdjustmentMonths = data?.rentalAdjustmentMonths
        }

        return { url: `/projects/${projectId}/bookingForm`, method: "PUT", body: body }
      },
    }),

    addEditProjectContractExpiryRules: build.mutation({
      query: ({ data, projectId }) => {
        let transformedExpiryRules = data?.contractExpiryRules?.map(item => ({
          ruleType: item?.paymentType?.value,
          valueBy: item?.amountType?.value,
          value: parseFloat(item?.contractValue),
          expiryFrom: item?.applicableFrom?.value,
          durationHours: parseInt(item?.expiry) * 24,
          logic: "greaterThanInclusive",
        }))
        let transformedMortgageRules = data?.mortgageExpiryRules?.map(item => ({
          ruleType: item?.paymentType?.value,
          valueBy: item?.amountType?.value,
          value: parseFloat(item?.contractValue),
          expiryFrom: item?.applicableFrom?.value,
          durationHours: parseInt(item?.expiry) * 24,
          logic: "greaterThanInclusive",
        }))

        let body = {
          projectId: projectId,
          rules: data?.contractExpiryRules ? transformedExpiryRules : [],
          mortgageRules: data?.mortgageExpiryRules ? transformedMortgageRules : [],
          expirySlabLock: data?.expirySlabLock,
        }
        return { url: `/projects/addCustomExpiryRules`, method: "POST", body: body }
      },
      invalidatesTags: ["project-expiry-rules"],
    }),

    addUpdateProjectInvoiceTax: build.mutation({
      query: ({
        projectFinancialId,
        projectId,
        payload,
        developerId,
        policies,
        selectedPolicies,
        policyFormObj,
        notification,
      }) => {
        let notificationObj = payload?.notifications?.map(item => {
          let tempObj = notification?.find(
            obj => obj?.id == item?.PaymentStatus?.value || obj?.id == item?.PaymentStatus?.id
          )
          return {
            ...item,
            PaymentStatus: tempObj,
          }
        })
        let vasFees = payload?.vasFees?.map(item => ({
          amountValue: item?.amount,
          amountBy: item?.amountType?.value,
          isMandatory: item?.type?.value === "mandatory",
          taxPercentage: item?.tax,
          key: item?.serviceType?.key,
        }))
        let filteredpolicyObj = policies?.filter(item => selectedPolicies?.includes(item?.id))
        let finalPolicy = filteredpolicyObj?.map(item => {
          const policyData = policyFormObj?.[item?.id]
          const policyConfigs = policyData?.policies
          return {
            ...item,
            policyConfigs: policyConfigs,
            isTimeBoundPolicy: policyData?.isTimeBoundPolicy,
            ...(policyData?.policyEndDate && {
              policyEndDate: dateTimeFunction({ date: policyData?.policyEndDate, timeZone: true }),
            }),
            ...(policyData?.isTimeBoundPolicy && {
              recipients: policyData?.recipients?.split(",")?.map(email => email.trim()),
            }),
          }
        })
        let bookingDispatchEmails = (payload?.bookingDispatchNotificationsEmails ?? []).map(row => row?.email)
        let body = {
          invoiceBankAccounts: payload?.invoiceBank?.map(bank => {
            return { ...bank, paymentType: bank?.paymentType?.value || bank?.paymentType }
          }),
          billingTypeId: payload?.invoiceType,
          paymentNotifications: notificationObj,
          bookingDispatchNotificationsEmails: bookingDispatchEmails,
          ...(payload?.incentiveDimbursementDelay === "custom" && { incentiveDelay: payload?.delay }),
          bookingPlatforms: payload?.bookingPlatforms,
          projectDeveloper: {
            id: developerId,
            invoicePoc: [{ email: payload?.pocEmail, name: payload?.pocName, phone: payload?.pocPhone }],
            agencyAccountName: payload?.agencyPoc,
            agencyNtnNumber: payload?.agencyNTN,
            invoicePaymentModes: payload?.invoicePaymentModes?.toString(),
            invoiceTax: payload?.invoiceTax,
            agencyTax: payload?.agencyTax,
            totalTax: payload?.totalTax,
            ntnNumber: payload?.developerNTN,
            accountName: payload?.developerPoc,
          },
          issuePaymentReceipt: !!payload?.issuePaymentReceipt,
          partialDp: payload?.pdp,
          paymentMode: payload?.projectPaymentModes?.toString(),
          minToken: +payload?.token,
          minTokenUnit: payload?.tokenType && (payload?.tokenType == "%" ? "percentage" : "amount"),
          ...(payload?.mortgageSaleAllowed && {
            minTokenMortgage: +payload?.mortgageToken,
            minTokenUnitMortgage:
              payload?.mortgageTokenType && (payload?.mortgageTokenType === "%" ? "percentage" : "amount"),
          }),
          paymentProof: payload?.paymentProof && payload?.paymentProof === "on" ? true : false,
          projectFinancial: {
            ...(projectFinancialId && { id: projectFinancialId }),
            pricePremium: payload?.directPremiumSharing,
            ...(payload?.affiliatePremiumSharing && { affiliatePricePremium: payload?.affiliatePremiumSharing }),
            claimableSchedules: payload?.billableSchedule?.map(item => {
              let obj = {}
              Object.keys(item)?.forEach(key => {
                obj = {
                  ...obj,
                  [key]: key === "premium" && !item[key] ? 0 : +item[key],
                }
              })
              return obj
            }),
            completeDownPayment: payload?.cdp,
            priceMarketing: payload?.marketingFee?.val,
            priceType: payload?.marketingFee?.option,
            mktPriceService: payload?.serviceFee,
            mktPriceMarketingCore: payload?.MFC,
            mktPriceSalesFlat: payload?.flatFeeSales,
            pricePerLead: payload?.pricePerLead,
            policies: finalPolicy,
          },
          requiresCNIC: !!payload?.requiresCNIC,
          requiresCountry: !!payload?.requiresCountry,
          requiresNationality: !!payload?.requiresNationality,
          requiresCity: !!payload?.requiresCity,
          requiresDOB: !!payload?.requireDOB,
          requireEmail: !!payload?.requireEmail,
          requireLocality: !!payload?.requireLocality,
          isRAFlowEnabled: !!payload?.isRAFlowEnabled,
          vasFeeEnabled: !!payload?.vasFeeEnabled,
          ...(payload?.vasFees && {
            vasFees,
          }),
          ...(payload?.mortgageSaleAllowed && { mortgageSaleAllowed: payload?.mortgageSaleAllowed }),
        }
        return {
          url: `/projects/${projectId}/invoicingAndTaxation`,
          method: projectFinancialId ? "PUT" : "POST",
          body: body,
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["developer-accounts"] : []
      },
    }),

    addUpdateProjectDeveloperInfo: build.mutation({
      query: ({ projectId, developerId, ...rest }) => {
        let body = {
          ...rest,
          logo: {
            ...rest?.logo,
            file: rest?.logo?.file?.split(",")[1] || rest?.logo?.file,
          },
        }

        return {
          url: developerId
            ? `/projects/${projectId}/developerInfo/${developerId}`
            : `/projects/${projectId}/developerInfo`,
          method: developerId ? "PUT" : "POST",
          body: body,
        }
      },
    }),

    addUpdateProjectDiscounts: build.mutation({
      query: ({ projectId, ...values }) => {
        return {
          url: `/projects/${projectId}/discounts`,
          method: "PUT",
          body: values,
        }
      },
    }),

    // TODO: use query update for real time update on marking favorite and unfavorite

    markProjectFavorite: build.mutation({
      query: payload => {
        return { url: `/favoriteProjects/favorite`, method: "PUT", body: payload }
      },
      invalidatesTags: (result, error, args) => {
        return result ? ["projects-listing"] : []
      },
    }),
    markProjectUnFavorite: build.mutation({
      query: payload => {
        return { url: `/favoriteProjects/unfavorite`, method: "DELETE", body: payload }
      },
      invalidatesTags: (result, error, args) => {
        return result ? ["projects-listing"] : []
      },
    }),

    deleteMedia: build.mutation({
      query: id => {
        return { url: `/projects/media/${id}`, method: "DELETE" }
      },
    }),

    updateImageTagging: build.mutation({
      query: ({ values }) => {
        return {
          url: `/projects/media/${values?.id}`,
          method: "PUT",
          body: values,
        }
      },
    }),

    getAllocatedStaff: build.query({
      query: params => `/projects/${params}/users`,
    }),
    allocateStaff: build.mutation({
      query: ({ projectId, ...values }) => {
        return {
          url: `/projects/${projectId}/updateAllocatedUsers`,
          method: "PUT",
          body: values,
        }
      },
    }),
    getProjectCategories: build.query({
      query: () => `/projecttypes`,
      transformResponse: data => projectCategoriesTransformer(data),
    }),
    addEditProjectBookingFormConfiguration: build.mutation({
      query: ({
        values,
        transformedTermsAndConditions,
        transformedTermsAndConditionsAR,
        projectId,
        frontCoverKey,
        backCoverKey,
        needsBookingForm,
        autoGenerateBookingForm,
        autoGenerateBookingFormStatus,
      }) => {
        const coverPages = coverPageMapper(
          [frontCoverKey, backCoverKey],
          [values?.frontCoverPage, values?.backCoverPage]
        )
        let termsAndConditions =
          values?.bookingFormTermCondition === "<p><br></p>" || values?.bookingFormTermCondition === null
            ? ""
            : values?.bookingFormTermCondition
        let transformedTerms =
          transformedTermsAndConditions === "<p><br></p>" || transformedTermsAndConditions === null
            ? ""
            : transformedTermsAndConditions

        let termsAndConditionsAR =
          values?.bookingFormTermConditionAR === "<p><br></p>" || values?.bookingFormTermConditionAR === null
            ? ""
            : values?.bookingFormTermConditionAR
        let transformedTermsAR =
          transformedTermsAndConditionsAR === "<p><br></p>" || transformedTermsAndConditionsAR === null
            ? ""
            : transformedTermsAndConditionsAR
        let body = {
          termsAndConditions: {
            termsAndConditions: sanitizeHTML(termsAndConditions),
            transformedTermsAndConditions: transformedTerms,
          },
          ...(termsAndConditionsAR && {
            termsAndConditionsAR: {
              termsAndConditions: sanitizeHTML(termsAndConditionsAR),
              transformedTermsAndConditions: transformedTermsAR,
            },
          }),
          isNomineeRequired: values?.nominee,
          coverPage: coverPages,
          cancellationFee: values?.cancellationFee ?? 15,
          needsBookingForm: needsBookingForm,
          autoGenerateBookingForm: autoGenerateBookingForm,
          needsRegaContract: values?.needsRegaContract || false,
          ...(autoGenerateBookingForm && { autoGenerateBookingFormStatus: autoGenerateBookingFormStatus }),
        }
        return {
          url: `/projects/${projectId}/bookingFormConfiguration`,
          method: "PUT",
          body: body,
        }
      },
    }),
    getDefaultTermsAndConditions: build.query({
      query: () => `/projects/termsAndConditions`,
      transformResponse: response => response?.data,
    }),
    getSmartKeys: build.query({
      query: params => `/smartKeys?${convertQueryObjToString(params)}`,
    }),
    getPreviewPaymentsTermsAndConditions: build.mutation({
      query: ({ body }) => ({
        url: "/projects/preview/paymentTermsAndConditions",
        method: "POST",
        body: body,
        responseHandler: response => (response.ok ? response.arrayBuffer() : response.json()),
        headers: { accept: "application/pdf" },
      }),
    }),
    getProjectPromoTags: build.query({
      query: id => `/projects/${id}/promoTags`,
      transformResponse: response => projectPromoTagsTransformer(response),
    }),
    getBookingPlatforms: build.query({
      query: () => `/bookingPlatforms`,
      transformResponse: data => bookingPlatformTransformer(data),
    }),
    getPaymentTermsAndConditions: build.query({
      query: params => `/projects/paymentTermsAndConditions?${convertQueryObjToString(params)}`,
    }),
    getProjectDevelopers: build.query({
      query: () => `/projects/developers`,
      transformResponse: data => projectDevelopersTransformer(data),
    }),
    getProjectDeveloperAccounts: build.query({
      query: developerId => `/projects/accounts/${developerId}`,
      transformResponse: resp =>
        resp?.data?.map(bank => ({
          ...bank,
          paymentType: capitalizeFirstLetter(bank?.paymentType),
          key: `${bank?.accountNumber}-${bank?.id}`,
        })),
      providesTags: ["developer-accounts"],
    }),
    deleteBankAccount: build.mutation({
      query: ({ id }) => {
        return { url: `/projects/bankAccounts/${id}`, method: "DELETE" }
      },
    }),
    syncWithEscrow: build.mutation({
      query: payload => {
        return { url: `/projects/dhamenSync`, method: "POST", body: payload }
      },
    }),
    getPreviewTermsAndConditions: build.mutation({
      query: ({ body }) => ({
        url: `/projects/previewTermsAndConditions`,
        method: "POST",
        body: body,
        responseHandler: response => (response.ok ? response.arrayBuffer() : response.json()),
        headers: { accept: "application/pdf" },
      }),
    }),
    getVasServices: build.query({
      query: () => `/vasFees`,
      transformResponse: resp =>
        resp?.map(item => ({
          ...item,
          name: localisedValue({ object: item, key: "name" }),
        })),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetListingProjectsQuery,
  useLazyGetListingProjectsQuery,
  useGetUnitTypesWithSubTypesQuery,
  useGetProjectDetailFormQuery,
  useGetDeveloperPortalStepperPrefillQuery,
  useSubmitDeveloperPortalStepperMutation,
  useUpdateDeveloperPortalProjectsMutation,
  useDisconnectDeveloperPortalMutation,
  useLazyGetProjectDetailFormQuery,
  useGetProjectBasicInfoQuery,
  useAddProjectBasicInfoMutation,
  useUpdateProjectSettingsMutation,
  useUpdateProjectMediaMutation,
  useGetPaymentModesInvoiceTaxQuery,
  useGetProjectPaymentModeQuery,
  useGetPoliciesQuery,
  useGetPaymentStatusesQuery,
  useGetProjectTypesQuery,
  useGetProjectTypeLocationsQuery,
  useLazyGetProjectTypeLocationsQuery,
  useLazyGetProjectTypesQuery,
  useGetProjectPhaseListingQuery,
  useLazyGetProjectSectionUnitTypeQuery,
  useGetUnitTypesByListingQuery,
  useLazyGetUnitTypesByListingQuery,
  useLazyGetProjectPhaseFormDataQuery,
  useGetProjectDesignationsQuery,
  useDeleteProjectPhaseMutation,
  useAddProjectPhaseMutation,
  useAddUpdateProjectInvoiceTaxMutation,
  useAddUpdateProjectDeveloperInfoMutation,
  useMarkProjectFavoriteMutation,
  useMarkProjectUnFavoriteMutation,
  useAddEditProjectBookingFormMutation,
  useAddUpdateProjectDiscountsMutation,
  useLazyGetAllocatedStaffQuery,
  useAllocateStaffMutation,
  useGetProjectCategoriesQuery,
  useGetProjectContractExpiryRulesQuery,
  useAddEditProjectContractExpiryRulesMutation,
  useLazyGetUnitTypesWithSubTypesQuery,
  useLazyGetAreaBasedOnLocationAndUnitQuery,
  useDeleteMediaMutation,
  useUpdateImageTaggingMutation,
  useAddEditProjectBookingFormConfigurationMutation,
  useGetDefaultTermsAndConditionsQuery,
  useLazyGetDefaultTermsAndConditionsQuery,
  useGetSmartKeysQuery,
  useLazyGetSmartKeysQuery,
  useGetPreviewTermsAndConditionsMutation,
  useLazyGetProjectPromoTagsQuery,
  useGetBookingPlatformsQuery,
  useLazyGetBookingPlatformsQuery,
  useGetProjectClassificationsQuery,
  useLazyGetPaymentTermsAndConditionsQuery,
  useGetProjectDevelopersQuery,
  useGetProjectDeveloperAccountsQuery,
  useDeleteBankAccountMutation,
  useSyncWithEscrowMutation,
  useGetPreviewPaymentsTermsAndConditionsMutation,
  useGetVasServicesQuery,
} = projectApis
